import {
  ModalCloseButton,
  createStackHeaderNavigator,
} from "@towersystems/roam-common-native";
import {
  ProductActionsSelectVariantTemplateOptionValuesRootParamList,
  ProductActionsSelectVariantTemplateRootParamList,
} from "../../../../types";
import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import { ProductActionsSelectVariantTemplateOptionValuesScreen } from "../ProductActionsSelectVariantTemplateOptionValuesScreen";
import { useHasTransitionEnded } from "../../../../utilities/use-after-interactions";
import {
  QUERY_PRODUCT_OPTION_GROUP_TEMPLATE,
  useSelectedProductEntityContextProvider,
} from "../../utils";
import { useLazyQuery } from "@apollo/client";
import {
  Product,
  ProductOptionGroupTemplate,
} from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { PlaceholderLoadingScreen } from "../PlaceholderLoadingScreen";
import { PageErrorOccurred } from "../../../../components";
import { CreateVariantsFromTemplateFormProvider } from "../../components/CreateVariantsFromTemplateForm/CreateVariantsFromTemplateFormProvider";
import { CreateVariantsFromTemplateFormValues } from "../../components/CreateVariantsFromTemplateForm/types";

export type ProductActionsSelectVariantTemplateOptionValuesRootScreenProps =
  StackScreenProps<
    ProductActionsSelectVariantTemplateRootParamList,
    "ProductActionsSelectVariantTemplateOptionValuesRootScreen"
  >;

export const ProductActionsSelectVariantTemplateOptionValuesRootScreen = ({
  navigation,
  route,
}: ProductActionsSelectVariantTemplateOptionValuesRootScreenProps) => {
  const transitionEnded = useHasTransitionEnded(navigation);
  const { templateId } = route.params;
  const { product } = useSelectedProductEntityContextProvider();

  const [q, { data, error }] = useLazyQuery<{
    productOptionGroupTemplate: ProductOptionGroupTemplate;
  }>(QUERY_PRODUCT_OPTION_GROUP_TEMPLATE);

  React.useEffect(() => {
    q({
      variables: {
        id: templateId,
      },
    }).catch((err) => console.log(err));
  }, []);

  const RootNavigator =
    createStackHeaderNavigator<ProductActionsSelectVariantTemplateOptionValuesRootParamList>();
  const item = data?.productOptionGroupTemplate;

  // Wait for transition to end before rendering the screen
  // otherwise the screen animation will be janky
  if ((!item && !error) || false == transitionEnded) {
    return <PlaceholderLoadingScreen navigation={navigation} />;
  }

  if (!item || error) {
    return (
      <PageErrorOccurred
        errorMessage={error?.message ?? "Something went wrong"}
        onRetry={() => {
          navigation.goBack();
        }}
      />
    );
  }

  return (
    <CreateVariantsFromTemplateFormProvider
      defaultValues={entityToFormValues(product, item)}
    >
      <RootNavigator.Navigator
        screenOptions={{
          headerShown: true,
          headerLeft: () => <ModalCloseButton type="Cancel" />,
          animationEnabled: true,
          title: "",
          cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
        }}
      >
        <RootNavigator.Screen
          name="ProductActionsSelectVariantTemplateOptionValuesScreen"
          component={ProductActionsSelectVariantTemplateOptionValuesScreen}
        />
      </RootNavigator.Navigator>
    </CreateVariantsFromTemplateFormProvider>
  );
};

const entityToFormValues = (
  product: Product,
  entity: ProductOptionGroupTemplate
): CreateVariantsFromTemplateFormValues => {
  return {
    id: product.id,
    optionGroupTemplateId: entity.id,
    selectedOptions: entity.options.map((ov) => ({
      name: ov.name,
      value: "",
    })),
  };
};
